import {
  ClientFileMessage,
  ClientUserMessage,
  // @ts-expect-error Sendbird does not provide types
} from "@sendbird/uikit-react/types/types";

import { fetchApi } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const getMessageTypeFromFile = (file: File): string => {
  const mimeType = file.type.toLowerCase();

  if (mimeType.startsWith("audio/")) {
    return "VOICE_MESSAGE";
  }

  if (mimeType.startsWith("image/")) {
    return "IMAGE_MESSAGE";
  }

  if (mimeType.startsWith("video/")) {
    return "VIDEO_MESSAGE";
  }

  return "MEDIA";
};

export const saveSentMessage = async (
  messageSent: ClientUserMessage | ClientFileMessage,
  file: File | null,
  to_user: string,
) => {
  try {
    if (!messageSent) return;

    const message = file
      ? (messageSent as ClientFileMessage).plainUrl
      : (messageSent as ClientUserMessage).message;

    const customType = file
      ? getMessageTypeFromFile(file)
      : messageSent.customType || "TEXT";

    const serviceParams: Record<string, string | File> = {
      sdk_id: messageSent.messageId,
      to_user: to_user,
      message_type: "text",
      message: message,
      custom_type: customType,
      data: file ? file : "",
    };

    const formData = new FormData();

    for (const key in serviceParams) formData.append(key, serviceParams[key]);

    await fetchApi({
      url: "v1/messages",
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: formData,
    });

    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
};

interface MessageResponse {
  signedUrl: string;
}

const getMessageBySdkId = async (sdk_id: string) => {
  try {
    const response = await fetchApi<MessageResponse>({
      url: `v1/messages/${sdk_id}`,
      method: "GET",
    });

    return response.data?.signedUrl || null;
  } catch (e) {
    console.log(e);
    return null;
  }
};

export const useGetMessageBySdkId = (sdk_id: string | null) => {
  return useQuery({
    queryKey: ["message", sdk_id],
    queryFn: () => getMessageBySdkId(sdk_id!),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    enabled: !!sdk_id,
  });
};

export const useHideMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, hidden }: { messageId: string; hidden: boolean }) =>
      fetchApi({
        url: `v2/admin/messages/${messageId}/hide`,
        method: 'PATCH',
        data: { hidden },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["message"],
      });
      queryClient.invalidateQueries({
        queryKey: ["messages"],
      });
      queryClient.invalidateQueries({
        queryKey: ["messages-from-start"],
      });
    },
  });
};
